import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from './auth.service';
import { InitiatePaymentRequest, InitiatePaymentRequestBody, InitiatePaymentResponse } from '../ridango.models';
import { Capacitor } from '@capacitor/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Browser } from '@capacitor/browser';
import Env from '../../env';

@Injectable({
    providedIn: 'root'
})
export class PaymentPortalService {
    private baseUrl: string;

    constructor(
        private http: HttpClient,
        private ridangoAuth: AuthService,
        private translateService: TranslateService,
        @Inject('ENV') private env: Env
    ) {
        this.baseUrl = `${env.CORE_API_URL}/customer/v3/region/${env.REGION_ID}/payment`;
    }

    addTripPurposeToCart(
        shoppingCartId: number,
        customerAccountId: number | string,
        tripPurpose: string
    ): Observable<any> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${shoppingCartId}/channel/${this.env.CHANNEL}/trip-purpose`;

        return this.http.put(url, { tripPurpose }, { params: { customerAccountId } });
    }

    initiatePayment(request: InitiatePaymentRequest): Observable<InitiatePaymentResponse> {
        const responseCallback = (response: InitiatePaymentResponse) => {
            this.handleRedirect(response, request);

            return response;
        };

        const lang = this.translateService.currentLang;
        let returnUrl = window.location.origin;
        let abortUrl = window.location.origin;

        if (this.env.PAYMENT_RETURN_URL) {
            returnUrl = this.env.PAYMENT_RETURN_URL;
            abortUrl = this.env.PAYMENT_RETURN_URL; // Same as return URL

            if (this.env.PAYMENT_RETURN_COMPONENT) {
                returnUrl += lang + this.env.PAYMENT_RETURN_COMPONENT;
            }
            if (this.env.PAYMENT_ABORT_COMPONENT) {
                abortUrl += lang + this.env.PAYMENT_ABORT_COMPONENT;
            }
        }

        if (this.env.IS_NEW_AFC) {
            const { merchantType, shoppingCartId, customerAccountId } = request;
            const url = new URL(
                `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${shoppingCartId}/channel/${this.env.CHANNEL}/payment`
            );
            if (!merchantType) {
                console.error(
                    '[PaymentPortalService] merchantType is required for WMB new AFC but was not provided',
                    request
                );
                throw new Error('MERCHANT_TYPE_REQUIRED');
            }
            url.searchParams.set('merchantType', merchantType);
            url.searchParams.set('lang', lang);
            url.searchParams.set('redirectUrl', returnUrl);
            url.searchParams.set('abortUrl', abortUrl);
            if (customerAccountId != null) {
                url.searchParams.set('customerAccountId', String(customerAccountId));
            }

            return this.http.get<InitiatePaymentResponse>(url.toString()).pipe(
                map((response) => {
                    if (request.merchantType !== 'INVOICE') {
                        this.handleRedirect(response, request);
                    }
                    return response;
                })
            );
        } else {
            const url = `${this.baseUrl}/${request.paymentId}/cart/${request.shoppingCartId}`;

            if (request.ticketId) {
                returnUrl += `/ticketId/${request.ticketId}`;
            }
            if (request.bankCardPersonalize) {
                returnUrl += `/bankCardPersonalize/`;
            }
            const payload: InitiatePaymentRequestBody = {
                returnUrl: returnUrl,
                abortUrl: abortUrl,
                lang: this.translateService.currentLang
            };

            const token = this.ridangoAuth.getLocalAccessToken();
            let httpHeaders: HttpHeaders = null;
            if (token) {
                httpHeaders = new HttpHeaders({
                    Authorization: `Bearer ${token}`
                });
            }

            return this.http
                .post<InitiatePaymentResponse>(url, payload, { headers: httpHeaders })
                .pipe(map(responseCallback));
        }
    }

    private handleRedirect(response: InitiatePaymentResponse, request: InitiatePaymentRequest): void {
        const urlBuilder = new URL(response.paymentUrl);

        Object.entries(request.queryParams ?? {}).forEach(([key, value]) => {
            urlBuilder.searchParams.set(key, value);
        });

        const url = urlBuilder.toString();

        if (Capacitor.isNativePlatform()) {
            Browser.open({ url });
        } else {
            window.open(url, '_self');
        }
    }
}
